import { ChatBadge, ChatMessage, YouTubeRoles, fallbackColor } from 'lib/twitchChatParse'
import { EmoteCatalog, EmoteEntry, EmoteProvider, nativeEmoteUrlLarge } from 'lib/thirdPartyEmotes'
import { lineIsBlocked, matcherFor } from 'lib/chatModeration'
import { sampleBlockedByList } from 'lib/wallBlockedWords'

export const SAMPLE_OPACITY = 0.55
export const SAMPLE_SLOT_MS = 2200
export const SAMPLE_JITTER_MS = 1400
export const SAMPLE_WINDOW = 30
export const SAMPLE_STATIC_LINES = 8
export const WALL_MIN_INTERVAL_MS = 90
export const WALL_MAX_INTERVAL_MS = 1500
export const WALL_STATIC_SPRITES = 12
export const WALL_POOL_MAX = 48
export const DEFAULT_SAMPLE_SEED = 0x5eed

export const TWITCH_GLOBAL_SAMPLE_EMOTES: readonly string[] = Object.freeze(['25', '305954156', '425618', '30259', '64138', '81274'])

const TWITCH_EMOTE_NAMES: Readonly<Record<string, string>> = Object.freeze({
  25: 'Kappa',
  305954156: 'PogChamp',
  425618: 'LUL',
  30259: 'HeyGuys',
  64138: 'SeemsGood',
  81274: 'VoHiYo'
})

export const SAMPLE_BADGE_ART: Readonly<Record<string, string>> = Object.freeze({
  'moderator/1': 'https://static-cdn.jtvnw.net/badges/v1/3267646d-33f0-4b17-b3df-f923a41db1d0/3',
  'vip/1': 'https://static-cdn.jtvnw.net/badges/v1/b817aba4-fad8-49e2-b88a-7cc744dfa6ec/3',
  'subscriber/0': 'https://static-cdn.jtvnw.net/badges/v1/5d9f2208-5dd8-11e7-8513-2ff4adfae661/3',
  'premium/1': 'https://static-cdn.jtvnw.net/badges/v1/bbbe0db0-a598-423e-86d0-f9fb98ca1933/3'
})

const mulberry32 = (seed: number): (() => number) => {
  let a = seed >>> 0
  return (): number => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const fnv1a32 = (s: string): number =>
  Array.from(s).reduce((h, ch) => {
    const code = ch.codePointAt(0) ?? 0
    return Math.imul(h ^ code, 0x01000193) >>> 0
  }, 0x811c9dc5)

export const elementSeed = (baseSeed: number, id: string): number =>
  (fnv1a32(id === '' ? 'mockup' : id) ^ baseSeed) >>> 0

export const rngFor = (seed: number, n: number): (() => number) =>
  mulberry32((seed ^ Math.imul(n, 0x9e3779b1)) >>> 0)

const mod = (n: number, m: number): number => ((n % m) + m) % m

interface SampleTwitchChatter {
  name: string
  color: string
  badges: readonly ChatBadge[]
}

interface SampleYouTubeChatter {
  name: string
  role: 'moderator' | 'member' | 'none'
}

interface SampleLine {
  text: string
  emote?: string
  action?: boolean
}

const TWITCH_CHATTERS: readonly SampleTwitchChatter[] = Object.freeze([
  { name: 'Pixel_Penguin', color: '#FF4500', badges: [{ set: 'moderator', version: '1' }, { set: 'subscriber', version: '0' }] },
  { name: 'quietquasar', color: '#1E90FF', badges: [{ set: 'vip', version: '1' }] },
  { name: 'MossyBoulder', color: '#2E8B57', badges: [] },
  { name: 'lantern_fish', color: '#DAA520', badges: [{ set: 'subscriber', version: '0' }, { set: 'premium', version: '1' }] },
  { name: 'VioletVortex9', color: '#8A2BE2', badges: [] },
  { name: 'TidalTurnip', color: '#D2691E', badges: [{ set: 'subscriber', version: '0' }] },
  { name: 'nebula_noodle', color: '#FF69B4', badges: [] },
  { name: 'CopperKettle42', color: '#5F9EA0', badges: [{ set: 'moderator', version: '1' }] }
])

const YOUTUBE_CHATTERS: readonly SampleYouTubeChatter[] = Object.freeze([
  { name: 'CraftyComet', role: 'moderator' },
  { name: 'orbit_owl', role: 'none' },
  { name: 'Maple Lantern', role: 'member' }
])

const TWITCH_LINES: readonly SampleLine[] = Object.freeze([
  { text: 'that play was incredible' },
  { text: 'no shot they pull this back', emote: '25' },
  { text: 'POG', emote: '305954156' },
  { text: 'chat is this real' },
  { text: 'gg gg gg' },
  { text: 'first time catching this live, hi all', emote: '30259' },
  { text: 'the music today is so good' },
  { text: 'what a save', emote: '425618' },
  { text: 'hydrate check', emote: '64138' },
  { text: 'that was way too close' },
  { text: 'clip that' },
  { text: 'hello from the back row', emote: '81274' },
  { text: 'is this the final round?' },
  { text: 'lets goooo' },
  { text: 'big respect to both sides' },
  { text: 'waves at everyone', action: true }
])

const YOUTUBE_LINES: readonly string[] = Object.freeze([
  'hello from the YouTube side',
  'vote 2 vote 2 vote 2',
  'catching this on my lunch break',
  'audio sounds great today'
])

export interface SampleChatContext {
  seed: number
  twitchLogins: readonly string[]
  youtubeChannelIds: readonly string[]
  blockedWords: readonly string[]
}

const isBlocked = (text: string, name: string, blockedWords: readonly string[]): boolean => {
  if (blockedWords.length === 0) return false
  return lineIsBlocked(matcherFor(blockedWords), text, name)
}

const rolesOf = (role: SampleYouTubeChatter['role']): YouTubeRoles => ({
  owner: false,
  moderator: role === 'moderator',
  member: role === 'member',
  verified: false
})

const twitchLineMessage = (line: SampleLine): { text: string, emotes: ChatMessage['emotes'] } => {
  if (line.emote == null) return { text: line.text, emotes: [] }
  const name = TWITCH_EMOTE_NAMES[line.emote] ?? ''
  const start = Array.from(line.text).length + 1
  return {
    text: `${line.text} ${name}`,
    emotes: [{ id: line.emote, start, end: start + Array.from(name).length - 1 }]
  }
}

export const sampleChatMessage = (n: number, ctx: SampleChatContext): ChatMessage | null => {
  const hasYouTube = ctx.youtubeChannelIds.length > 0
  const hasTwitch = ctx.twitchLogins.length > 0
  const rng = rngFor(ctx.seed, n)
  if (hasYouTube && (!hasTwitch || mod(n, 4) === 2)) {
    const ordinal = hasTwitch ? Math.floor(n / 4) : n
    const i = Math.floor(rng() * YOUTUBE_CHATTERS.length)
    const chatter = YOUTUBE_CHATTERS[i]
    const text = YOUTUBE_LINES[mod(ordinal * 7 + ctx.seed, YOUTUBE_LINES.length)]
    if (isBlocked(text, chatter.name, ctx.blockedWords)) return null
    const color = fallbackColor(chatter.name)
    const source = ctx.youtubeChannelIds[mod(ordinal, ctx.youtubeChannelIds.length)]
    return {
      id: `sample-${n}`,
      platform: 'YOUTUBE',
      channel: '',
      roomId: '',
      login: `sample_yt_${i}`,
      displayName: chatter.name,
      color,
      badges: [],
      text,
      emotes: [],
      isAction: false,
      receivedAt: 0,
      publishedAt: 0,
      ...(source !== '' && { sourceChannelId: source }),
      authorAvatarUrl: sampleAvatarDataUri(chatter.name, color),
      youtubeRoles: rolesOf(chatter.role)
    }
  }
  const ordinal = hasYouTube ? n - Math.floor((n + 1) / 4) : n
  const i = Math.floor(rng() * TWITCH_CHATTERS.length)
  const chatter = TWITCH_CHATTERS[i]
  const line = TWITCH_LINES[mod(ordinal * 7 + ctx.seed, TWITCH_LINES.length)]
  const { text, emotes } = twitchLineMessage(line)
  if (isBlocked(text, chatter.name, ctx.blockedWords)) return null
  return {
    id: `sample-${n}`,
    platform: 'TWITCH',
    channel: hasTwitch ? ctx.twitchLogins[mod(ordinal, ctx.twitchLogins.length)] : '',
    roomId: '',
    login: `sample_${i}`,
    displayName: chatter.name,
    color: chatter.color,
    badges: chatter.badges.map((b) => ({ set: b.set, version: b.version })),
    text,
    emotes,
    isAction: line.action === true,
    receivedAt: 0,
    publishedAt: 0
  }
}

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/

const XML_ESCAPES: Readonly<Record<string, string>> = Object.freeze({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;'
})

const xmlEscape = (s: string): string => s.replace(/[&<>"']/g, (c) => XML_ESCAPES[c] ?? '')

export const sampleAvatarDataUri = (name: string, color: string): string => {
  const fill = HEX_COLOR.test(color) ? color : '#808080'
  const first = Array.from(name.trim())[0]
  const initial = xmlEscape(first == null ? '?' : first.toUpperCase())
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36">' +
    `<circle cx="18" cy="18" r="18" fill="${fill}"/>` +
    '<text x="18" y="24.5" text-anchor="middle" font-family="ui-sans-serif, system-ui, sans-serif" ' +
    `font-size="18" font-weight="700" fill="#ffffff">${initial}</text></svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

const JITTER_SALT = 0x6a177e5

export const sampleAppearAt = (k: number, seed: number): number =>
  k * SAMPLE_SLOT_MS + Math.floor(rngFor(seed ^ JITTER_SALT, k)() * SAMPLE_JITTER_MS)

export const sampleHeadAt = (nowMs: number, seed: number): number => {
  const k = Math.floor(nowMs / SAMPLE_SLOT_MS)
  return sampleAppearAt(k, seed) <= nowMs ? k : k - 1
}

export const sampleWindowAt = (nowMs: number, seed: number, size: number = SAMPLE_WINDOW): number[] => {
  const head = sampleHeadAt(nowMs, seed)
  if (head < 0 || size <= 0) return []
  const from = Math.max(0, head - size + 1)
  return Array.from({ length: head - from + 1 }, (_, i) => from + i)
}

export const sampleNextChangeAt = (nowMs: number, seed: number): number =>
  sampleAppearAt(sampleHeadAt(nowMs, seed) + 1, seed)

const STATIC_WINDOW: readonly number[] = Object.freeze(Array.from({ length: SAMPLE_STATIC_LINES }, (_, i) => i))

export const staticSampleWindow = (): readonly number[] => STATIC_WINDOW

export interface WallSampleEmote {
  url: string
  name: string
  provider: 'TWITCH' | 'YOUTUBE' | EmoteProvider
  emoji?: string
}

export interface WallSamplePoolConfig {
  showTwitchEmotes: boolean
  show7tvEmotes?: boolean | null
  showBttvEmotes?: boolean | null
  showFfzEmotes?: boolean | null
  youtube?: boolean
  blockedWords?: readonly string[] | null
}

export const YOUTUBE_WALL_SAMPLE_EMOJI: readonly string[] = Object.freeze(['😂', '🔥', '❤️', '👍', '🎉', '😍', '💜', '👏', '😮', '🙌', '💯', '😎', '🥳', '✨', '🤣', '👀'])

export const wallSampleKeyOf = (e: Pick<WallSampleEmote, 'url' | 'emoji'>): string => e.emoji ?? e.url

const codeUnitCompare = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

const providerEnabled = (cfg: WallSamplePoolConfig, provider: EmoteProvider): boolean => {
  if (provider === '7TV') return cfg.show7tvEmotes !== false
  if (provider === 'BTTV') return cfg.showBttvEmotes !== false
  return cfg.showFfzEmotes !== false
}

const wallArtOf = (e: EmoteEntry): string => e.urls.x4 ?? e.urls.x2

const thin = <T>(list: readonly T[], max: number, seed: number): T[] => {
  if (max <= 0) return []
  if (list.length <= max) return [...list]
  const step = list.length / max
  const start = rngFor(seed, 0x7417)() * step
  return Array.from({ length: max }, (_, i) => list[Math.min(list.length - 1, Math.floor(start + i * step))])
}

export const wallSamplePool = (cfg: WallSamplePoolConfig, catalog: EmoteCatalog | null, seed: number): WallSampleEmote[] => {
  const words = cfg.blockedWords ?? []
  if (words.length > 0) {
    return wallSamplePoolUnfiltered(cfg, catalog, seed).filter((e) => !sampleBlockedByList(words, e))
  }
  return wallSamplePoolUnfiltered(cfg, catalog, seed)
}

const wallSamplePoolUnfiltered = (cfg: WallSamplePoolConfig, catalog: EmoteCatalog | null, seed: number): WallSampleEmote[] => {
  if (cfg.youtube === true) {
    return YOUTUBE_WALL_SAMPLE_EMOJI.map((emoji): WallSampleEmote => ({ url: '', name: emoji, provider: 'YOUTUBE', emoji }))
  }
  const twitch: WallSampleEmote[] = cfg.showTwitchEmotes
    ? TWITCH_GLOBAL_SAMPLE_EMOTES.map((id) => ({ url: nativeEmoteUrlLarge(id), name: TWITCH_EMOTE_NAMES[id] ?? id, provider: 'TWITCH' }))
    : []
  const entries = catalog == null ? [] : [...catalog.channel, ...catalog.global]
  const seenUrls = new Set<string>()
  const third = entries
    .filter((e) => !e.zeroWidth && providerEnabled(cfg, e.provider))
    .map((e): WallSampleEmote => ({ url: wallArtOf(e), name: e.name, provider: e.provider }))
    .filter((e) => {
      if (seenUrls.has(e.url)) return false
      seenUrls.add(e.url)
      return true
    })
    .sort((a, b) => {
      const byProvider = codeUnitCompare(a.provider, b.provider)
      return byProvider !== 0 ? byProvider : codeUnitCompare(a.name, b.name)
    })
  return [...twitch, ...thin(third, WALL_POOL_MAX - twitch.length, seed)]
}

export const wallSampleInterval = (density: number, durationSeconds: number): number => {
  const d = Number.isFinite(density) && density > 0 ? density : 1
  const s = Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 1
  return Math.min(WALL_MAX_INTERVAL_MS, Math.max(WALL_MIN_INTERVAL_MS, Math.round(s * 1000 / d)))
}

export interface WallSampleSlot {
  k: number
  poolIndex: number
  leftPct: number
  topPct: number
  driftPx: number
  sizeFactor: number
}

const WALL_SALT = 0x3a11

export const wallSampleSlot = (k: number, seed: number, poolSize: number): WallSampleSlot => {
  const rng = rngFor(seed ^ WALL_SALT, k)
  const pick = rng()
  return {
    k,
    poolIndex: poolSize > 0 ? Math.min(poolSize - 1, Math.floor(pick * poolSize)) : 0,
    leftPct: 2 + rng() * 92,
    topPct: 8 + rng() * 76,
    driftPx: Math.round((rng() * 2 - 1) * 140),
    sizeFactor: 0.75 + rng() * 0.5
  }
}

const MAX_SLOTS_LISTED = 512

export const wallSlotsBetween = (fromMs: number, toMs: number, intervalMs: number): number[] => {
  if (!(intervalMs > 0) || !(toMs > fromMs)) return []
  const last = Math.floor(toMs / intervalMs)
  const first = Math.max(Math.floor(fromMs / intervalMs) + 1, last - MAX_SLOTS_LISTED + 1)
  if (last < first) return []
  return Array.from({ length: last - first + 1 }, (_, i) => first + i)
}
